<div class="unit-type-card" [class.collapsed]="!isExpanded()" [class.has-errors]="hasErrors()" *transloco="let t" data-testid="unit-type-card">
  <!-- Unit Header -->
  <div class="unit-header">
    <div class="header-left">
      <h3 class="unit-title">
        {{ t('objects.wizard.unitTypes.typeNumber', { index: index() + 1, name: form.get('name')?.value || t('objects.wizard.unitTypes.newType') }) }}
      </h3>
      @if (hasErrors()) {
        <i
          class="pi pi-exclamation-circle error-icon"
          [pTooltip]="errorsTooltip()"
          tooltipPosition="right"
          [escape]="false"
        ></i>
        @if (!isExpanded()) {
          <span class="error-count">{{ validationErrors().length }} {{ validationErrors().length === 1 ? t('objects.wizard.unitTypes.problemSingular') : t('objects.wizard.unitTypes.problemPlural') }}</span>
        }
      }
    </div>
    <div class="header-actions">
      <p-button
        data-testid="unit-card-edit-btn"
        [label]="isExpanded() ? t('objects.wizard.unitTypes.collapse') : t('objects.wizard.unitTypes.editButton')"
        [icon]="isExpanded() ? 'pi pi-chevron-up' : 'pi pi-pencil'"
        [text]="true"
        severity="secondary"
        size="small"
        (onClick)="toggleExpanded()"
      />
      @if (canRemove()) {
        <p-button
          data-testid="unit-card-remove-btn"
          icon="pi pi-trash"
          [rounded]="true"
          [text]="true"
          severity="danger"
          size="small"
          [disabled]="disabled()"
          (onClick)="onRemove()"
        />
      }
    </div>
  </div>

  @if (!isExpanded() && summaryItems().length > 0) {
    <div class="unit-summary">
      @for (item of summaryItems(); track $index) {
        <span class="summary-chip">
          @if (item.label) {
            <span class="chip-label">{{ item.label }}:</span>
          }
          <span class="chip-value">{{ item.value }}</span>
        </span>
      }
    </div>
  }

  @if (isExpanded()) {
    <form [formGroup]="form" class="unit-form">
      <!-- Gallery -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.unitTypes.interiorPhotos', { name: form.get('name')?.value || t('objects.wizard.unitTypes.newType') }) }}</label>
        <app-gallery-upload
          data-testid="unit-card-gallery"
          [minImages]="limits.UNIT_TYPE_MIN_IMAGES"
          [maxImages]="limits.UNIT_TYPE_MAX_IMAGES"
          [disabled]="disabled()"
          [ngModel]="form.get('images')?.value"
          [ngModelOptions]="{standalone: true}"
          (imagesChange)="onImagesChange($event)"
          (uploadStateChange)="onGalleryUploadState($event)"
        />
        <small class="hint-text">
          {{ t('objects.wizard.gallery.unitImageLimitHint', { current: form.get('images')?.value?.length || 0, max: limits.UNIT_TYPE_MAX_IMAGES }) }}
        </small>
      </div>

      <!-- Property Type -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.propertyType.label') }}</label>
        <p-select
          data-testid="unit-card-property-type"
          formControlName="propertyTypeId"
          [options]="propertyTypes()"
          optionLabel="name"
          optionValue="id"
          [placeholder]="t('objects.wizard.propertyType.placeholder')"
          [fluid]="true"
          [showClear]="true"
        />
        @if (getErrorMessage('propertyTypeId'); as error) {
          <small class="field-error">{{ error }}</small>
        }
      </div>

      <!-- Main Fields Row -->
      <div class="fields-row">
        <div class="form-group">
          <label class="group-label">{{ t('objects.wizard.unitTypes.typeName') }}</label>
          <input pInputText id="name-{{index()}}" formControlName="name" data-testid="unit-card-name" [fluid]="true" />
          @if (getErrorMessage('name'); as error) {
            <small class="field-error">{{ error }}</small>
          }
        </div>

        <div class="form-group small">
          <label class="group-label">{{ t('objects.wizard.unitTypes.maxOccupancy') }}</label>
          <p-inputnumber data-testid="unit-card-max-occupancy" inputId="maxOccupancy-{{index()}}" formControlName="maxOccupancy" [min]="1" [max]="20" [fluid]="true" />
          @if (getErrorMessage('maxOccupancy'); as error) {
            <small class="field-error">{{ error }}</small>
          }
        </div>
      </div>

      <!-- Amenities -->
      <div class="form-group">
        <label class="group-label">{{ t('objects.wizard.unitTypes.unitAmenities') }}</label>
        @if (amenitiesLoading()) {
          <small class="hint-text">{{ t('objects.wizard.basicInfo.amenitiesLoading') }}</small>
        } @else {
          <app-amenity-selector
            data-testid="unit-card-amenities"
            label=""
            [amenities]="unitAmenities()"
            [disabled]="disabled()"
            [ngModel]="form.get('amenityIds')?.value"
            [ngModelOptions]="{standalone: true}"
            (selectionChange)="onAmenitiesChange($event)"
          />
        }
      </div>

      <!-- Room Details (optional) -->
      <div class="room-details-section">
        <label class="group-label">{{ t('objects.wizard.unitTypes.detailsSection') }}</label>
        <div class="fields-row">
          <div class="form-group small">
            <label class="group-label">{{ t('objects.wizard.unitTypes.squareMeters') }}</label>
            <p-inputnumber
              data-testid="unit-card-square-meters"
              inputId="squareMeters-{{index()}}"
              formControlName="squareMeters"
              [min]="1"
              [fluid]="true"
              suffix=" m²"
            />
          </div>
          <div class="form-group small">
            <label class="group-label">{{ t('objects.wizard.unitTypes.bedrooms') }}</label>
            <p-inputnumber
              data-testid="unit-card-bedrooms"
              inputId="bedrooms-{{index()}}"
              formControlName="bedrooms"
              [min]="0"
              [fluid]="true"
            />
          </div>
          <div class="form-group small">
            <label class="group-label">{{ t('objects.wizard.unitTypes.beds') }}</label>
            <p-inputnumber
              data-testid="unit-card-beds"
              inputId="beds-{{index()}}"
              formControlName="beds"
              [min]="0"
              [fluid]="true"
            />
          </div>
          <div class="form-group small">
            <label class="group-label">{{ t('objects.wizard.unitTypes.bathrooms') }}</label>
            <p-inputnumber
              data-testid="unit-card-bathrooms"
              inputId="bathrooms-{{index()}}"
              formControlName="bathrooms"
              [min]="0"
              [fluid]="true"
            />
          </div>
        </div>
      </div>

      <!-- Unit Naming -->
      <div class="form-group naming-section">
        <label class="group-label">{{ t('objects.wizard.unitTypes.unitNaming') }}</label>

        <div class="naming-help">
          <button type="button" class="naming-help__toggle" (click)="namingHelpExpanded = !namingHelpExpanded">
            <i class="pi pi-question-circle"></i>
            <span>{{ t('objects.wizard.unitTypes.namingHelpToggle') }}</span>
            <i class="pi" [class.pi-chevron-down]="!namingHelpExpanded" [class.pi-chevron-up]="namingHelpExpanded"></i>
          </button>
          @if (namingHelpExpanded) {
            <div class="naming-help__content">
              <p>{{ t('objects.wizard.unitTypes.namingHelpDescription') }}</p>
              <div class="naming-help__modes">
                <div class="naming-help__mode">
                  <strong><i class="pi pi-sync"></i> {{ t('objects.wizard.unitTypes.namingModeAuto') }}</strong>
                  <span>{{ t('objects.wizard.unitTypes.namingHelpAutoDesc') }}</span>
                </div>
                <div class="naming-help__mode">
                  <strong><i class="pi pi-pencil"></i> {{ t('objects.wizard.unitTypes.namingModeManual') }}</strong>
                  <span>{{ t('objects.wizard.unitTypes.namingHelpManualDesc') }}</span>
                </div>
              </div>
            </div>
          }
        </div>

        <p-selectbutton
          data-testid="unit-card-naming-mode"
          [options]="namingModeOptions()"
          formControlName="namingMode"
          optionLabel="label"
          optionValue="value"
          [allowEmpty]="false"
        />

        @if (namingMode === 'auto') {
          <small class="hint-text">{{ t('objects.wizard.unitTypes.namingBaseHint') }}</small>
          <div class="auto-naming-fields">
            <div class="form-group auto-naming-quantity">
              <label class="group-label">{{ t('objects.wizard.unitTypes.quantity') }}</label>
              <p-inputnumber data-testid="unit-card-quantity" inputId="quantity-{{index()}}" formControlName="quantity" [min]="1" [max]="100" [fluid]="true" />
              @if (getErrorMessage('quantity'); as error) {
                <small class="field-error">{{ error }}</small>
              }
            </div>
            <div class="form-group auto-naming-base">
              <label class="group-label">{{ t('objects.wizard.unitTypes.namingBase') }}</label>
              <input
                pInputText
                id="namingBase-{{index()}}"
                formControlName="namingBase"
                data-testid="unit-card-naming-base"
                [fluid]="true"
                [placeholder]="t('objects.wizard.unitTypes.namingBasePlaceholder')"
              />
              @if (getErrorMessage('namingBase'); as error) {
                <small class="field-error">{{ error }}</small>
              }
            </div>
          </div>

          @if (previewNames().length > 0) {
            <div class="naming-preview">
              <small class="hint-text">{{ t('objects.wizard.unitTypes.namingPreview') }}</small>
              <div class="preview-chips">
                @for (name of previewNames(); track $index) {
                  <span class="preview-chip-group">
                    <p-tag [value]="name" severity="secondary" />
                    @if (getReservationInfo($index); as info) {
                      @if (info.activeReservationCount > 0) {
                        <p-tag [value]="info.activeReservationCount + ' ' + t('objects.wizard.unitTypes.reservationsBadge')" severity="info" />
                      }
                      @if (info.futureReservationCount > 0) {
                        <p-tag [value]="info.futureReservationCount + ' ' + t('objects.wizard.unitTypes.futureShort')" severity="warn" />
                      }
                    }
                  </span>
                }
              </div>
            </div>
          }
        }

        @if (namingMode === 'manual') {
          <small class="hint-text">{{ t('objects.wizard.unitTypes.namingManualHint') }}</small>
          <div class="manual-names-list" cdkDropList (cdkDropListDropped)="onUnitDrop($event)" formArrayName="unitNames">
            @for (ctrl of unitNamesArray.controls; track ctrl) {
              <div class="manual-name-row" cdkDrag>
                <i class="pi pi-bars drag-handle" cdkDragHandle
                  [pTooltip]="t('objects.wizard.unitTypes.dragToReorder')"
                  tooltipPosition="left"
                ></i>
                <span class="name-index">{{ $index + 1 }}.</span>
                <input
                  pInputText
                  [formControlName]="$index"
                  [placeholder]="t('objects.wizard.unitTypes.unitNamePlaceholder', { index: $index + 1 })"
                  [attr.data-testid]="'unit-card-manual-name-' + $index"
                  [fluid]="true"
                />
                @if (getReservationInfo($index); as info) {
                  @if (info.activeReservationCount > 0) {
                    <p-tag [value]="info.activeReservationCount + ' ' + t('objects.wizard.unitTypes.reservationsBadge')" severity="info" />
                  }
                  @if (info.futureReservationCount > 0) {
                    <p-tag [value]="info.futureReservationCount + ' ' + t('objects.wizard.unitTypes.futureShort')" severity="warn" />
                  }
                }
                <p-button
                  icon="pi pi-times"
                  [rounded]="true"
                  [text]="true"
                  severity="danger"
                  size="small"
                  [disabled]="!canRemoveUnit($index)"
                  (onClick)="removeUnit($index)"
                  [pTooltip]="!canRemoveUnit($index) ? t('objects.wizard.unitTypes.cannotRemoveUnitActive') : t('objects.wizard.unitTypes.removeUnit')"
                  tooltipPosition="left"
                />
              </div>
            }
          </div>
          <p-button
            [label]="t('objects.wizard.unitTypes.addUnit')"
            icon="pi pi-plus"
            [text]="true"
            size="small"
            class="add-unit-btn"
            (onClick)="addUnit()"
          />
        }
      </div>

      <!-- Done button -->
      <div class="form-actions">
        @if (missingFields().length > 0) {
          <div class="missing-fields">
            <i class="pi pi-info-circle"></i>
            <span>{{ t('objects.wizard.unitTypes.fillRequired') }}</span>
            <ul>
              @for (field of missingFields(); track field) {
                <li>{{ field }}</li>
              }
            </ul>
          </div>
        }
        <div class="form-actions-buttons">
          <p-button
            data-testid="unit-card-cancel-btn"
            [label]="t('objects.wizard.unitTypes.cancelEditing')"
            icon="pi pi-times"
            severity="secondary"
            [text]="true"
            (onClick)="onCancel()"
          />
          <p-button
            data-testid="unit-card-done-btn"
            [label]="t('objects.wizard.unitTypes.doneEditing')"
            [icon]="isValid ? 'pi pi-check' : 'pi pi-exclamation-triangle'"
            [disabled]="!isValid"
            (onClick)="onDone()"
          />
        </div>
      </div>
    </form>
  }
</div>
